/* Config.js
 *
 * copyright (c) 2010-2026, Christian Mayer and the CometVisu contributors.
 *
 * This program is free software; you can redistribute it and/or modify it
 * under the terms of the GNU General Public License as published by the Free
 * Software Foundation; either version 3 of the License, or (at your option)
 * any later version.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with this program; if not, write to the Free Software Foundation, Inc.,
 * 59 Temple Place - Suite 330, Boston, MA  02111-1307, USA
 */

/**
 * Main settings that can be accessed from anywhere inside the Application
 */
qx.Class.define('cv.Config', {
  type: 'static',

  statics: {
    /**
     * Define ENUM of maturity levels for features, so that e.g. the editor can
     * ignore some widgets when they are not supported yet
     */
    Maturity: {
      release: 0,
      development: 1
    },

    /**
     * The current path tree
     * @type {String}
     */
    treePath: '',

    /**
     * Path to the current page
     * @type {String}
     */
    currentPageId: null,

    /**
     * @type {Boolean}
     */
    libraryCheck: true,
    /**
     * Threshold where the mobile.css is loaded
     * @type {Number}
     */
    maxMobileScreenWidth: 599,
    /**
     * Threshold where colspan-s is used
     * @type {Number}
     */
    maxScreenWidthColspanS: 599,
    /**
     * threshold where colspan-m is used
     * @type {Number}
     */
    maxScreenWidthColspanM: 839,
    /**
     * Default scrolling speed for page changes (in ms)
     * @type {Number}
     */
    scrollSpeed: 400,

    /**
     * Default number of colums the layout should use
     * @type {Number}
     */
    defaultColumns: 12,
    /**
     * Minimum column width
     * @type {Number}
     */
    minColumnWidth: 120,

    /**
     * If true, the client only loads the states for the widgets that are part of the start page at first
     * read request (should increase the performance when huge config files are used)
     * @type {Boolean}
     */
    enableAddressQueue: false,

    /**
     * Type of the used backend (*default*, *openhab* or *openhab2*)
     * @type {String}
     */
    backend: null,
    /**
     * Initial URL to the backend
     * @type {String}
     */
    backendLoginUrl: null,
    /**
     * @type {String}
     */
    configSuffix: null,
    /**
     * The design currently used
     * @type {String}
     */
    clientDesign: '',
    /**
     * The (optional) ID of this client instance
     * @type {String|null}
     */
    clientID: null,
    /**
     * Maturity level
     * @type {var}
     */
    use_maturity: false,

    /**
     * Default plugins to load, that are not controlled by the config (e.g. some backends can load own plugins)
     */
    pluginsToLoad: [],

    /**
     * Load the manager directly, no config
     * @type {boolean}
     */
    loadManager: false,

    /**
     * Optional settings for manager loading
     * @type {Map}
     */
    managerOptions: {},

    /**
     * Optional settings for backend defined by server
     */
    server: {},

    /**
     * All configuration and settings from the current configuration
     * (Note: all settings that need to be cached must be put in here)
     */
    configSettings: {
      mappings: {},
      stylings: {},
      /**
       * Stores the rowspans used by the current confid
       * @type {Map} of rowspan-value as key and true as value
       */
      usedRowspans: {},
      pluginsToLoad: [],

      /**
       * Array with alls icons defined in the current config file
       * @type {Array}
       */
      iconsFromConfig: [],
      /**
       * Credentials for Backend authentication, username/token and optional password
       */
      credentials: {
        username: null,
        password: null
      },

      bindClickToWidget: false
    },

    /**
     * Store last visited page in LocalStorage
     */
    rememberLastPage: false,

    /**
     * If enabled the widget instances are created on demand. Note: this must only be used when
     * cache is valid!
     * @type {Boolean}
     */
    lazyLoading: false,

    /**
     * Timeout till when all structure parts should be loaded. Only very slow
     * systems need to increase this value.
     * @type {number}
     */
    timeoutStructureLoad: 2000,

    /**
     * Defines which structure is supported by which designs
     */
    designStructureMap: {
      pure: [
        // broken: 'alaska',
        // broken: 'alaska_slim',
        'discreet',
        'discreet_sand',
        'discreet_slim',
        'metal',
        'pitchblack',
        // broken: 'planet',
        'pure'
      ],

      tile: ['tile']
    },

    /**
     * Currently loaded structure
     */
    loadedStructure: '',

    /**
     * Wether the error reporting with sentry is enabled or not
     */
    sentryEnabled: false,

    /**
     * If enabled the user interaction gets logged
     */
    reporting: false,

    /**
     * Set console logging
     */
    enableLogging: true,

    /**
     * The server that responded to the config request
     */
    configServer: null,

    /**
     * Only set in unit tests
     */
    unitTesting: false,

    /**
     * If the CometVisu can use service workers
     */
    useServiceWorker: false,

    enableServiceWorkerCache: true,

    defaultManifest: {
      'short_name': 'CometVisu',
      'name': 'CometVisu',
      'start_url': 'index.html',
      'theme_color': '#000',
      'background_color': '#000',
      'icons': [
        {
          'src': 'resource/icons/comet_webapp_icon_android_36.png',
          'sizes': '36x36',
          'type': 'image/png',
          'density': '0.75',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_48.png',
          'sizes': '48x48',
          'type': 'image/png',
          'density': '1.0',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_72.png',
          'sizes': '72x72',
          'type': 'image/png',
          'density': '1.5',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_96.png',
          'sizes': '96x96',
          'type': 'image/png',
          'density': '2.0',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_144.png',
          'sizes': '144x144',
          'type': 'image/png',
          'density': '3.0',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_192.png',
          'sizes': '192x192',
          'type': 'image/png',
          'density': '4.0',
          'purpose': 'any'
        },
        {
          'src': 'resource/icons/comet_webapp_icon_android_512.png',
          'sizes': '512x512',
          'type': 'image/png',
          'density': '4.0',
          'purpose': 'any'
        }
      ],
      'display': 'standalone',
      'orientation': 'any'
    },

    /**
     * Get the structure that is related to this design
     * @param design {String?} name of the design
     * @return {String} name of the structure
     */
    getStructure(design) {
      if (!design) {
        design = this.getDesign();
      }
      for (let structure in this.designStructureMap) {
        if (Object.prototype.hasOwnProperty.call(this.designStructureMap, structure)) {
          if (this.designStructureMap[structure].indexOf(design) >= 0) {
            return 'structure-' + structure;
          }
        }
      }
      // fallback to pure
      return 'structure-pure';
    },

    /**
     * This method tries to guess if the CometVisu is running on a proxied webserver.
     * (by comparing if the visu_config.xml-File has been delivered from another server than the
     * loging response). As this is just an assumption, you should not treat this result as reliable.
     */
    guessIfProxied() {
      if (this.configServer === null || cv.io.BackendConnections.getClient().getServer() === null) {
        throw new Error('not ready yet');
      }
      return this.configServer !== cv.io.BackendConnections.getClient().getServer();
    },

    addMapping(name, mapping) {
      this.configSettings.mappings[name] = mapping;
    },

    getMapping(name) {
      return this.configSettings.mappings[name];
    },

    hasMapping(name) {
      return Object.prototype.hasOwnProperty.call(this.configSettings.mappings, name);
    },

    clearMappings() {
      this.configSettings.mappings = {};
    },

    addStyling(name, styling) {
      this.configSettings.stylings[name] = styling;
    },

    getStyling(name) {
      return this.configSettings.stylings[name];
    },

    hasStyling(name) {
      return Object.prototype.hasOwnProperty.call(this.configSettings.stylings, name);
    },

    getDesign() {
      return this.clientDesign || this.configSettings.clientDesign;
    },

    /**
     * Enable error reporting with Sentry if it was requested and the Sentry SDK
     * was loaded completely.
     *
     * A partially loaded SDK (e.g. when only the rewriteframes bundle was
     * loaded) creates an incomplete `window.Sentry` object that lacks the core
     * API (like `setTag`). In that case error reporting is disabled instead of
     * crashing the whole startup with a TypeError.
     *
     * @param enabled {Boolean} whether error reporting was requested
     */
    enableErrorReporting(enabled) {
      if (enabled !== true) {
        return;
      }
      if (!window.Sentry || typeof window.Sentry.setTag !== 'function') {
        // Sentry SDK not (fully) loaded - degrade gracefully
        this.sentryEnabled = false;
        return;
      }
      this.sentryEnabled = true;
      // generate unique transactionId and set as Sentry tag
      this.transactionId = Math.random().toString(36).substr(2, 9);
      window.Sentry.setTag('transaction_id', this.transactionId);
      window.Sentry.setTag('build.date', cv.Version.DATE);
      window.Sentry.setTag('build.branch', cv.Version.BRANCH);
      Object.keys(cv.Version.TAGS).forEach(function (tag) {
        window.Sentry.setTag(tag, cv.Version.TAGS[tag]);
      });
    }
  },

  defer(statics) {
    const req = qx.util.Uri.parseUri(window.location.href);

    if (req.queryKey.timeoutStructureLoad) {
      cv.Config.timeoutStructureLoad = parseInt(req.queryKey.timeoutStructureLoad, 10);
    }

    if (req.queryKey.enableQueue) {
      cv.Config.enableAddressQueue = true;
    }

    if (req.queryKey.libraryCheck) {
      cv.Config.libraryCheck = req.queryKey.libraryCheck !== 'false'; // true unless set to false
    }

    if (req.queryKey.backend) {
      cv.Config.URL = { backend: req.queryKey.backend };
    } else {
      cv.Config.URL = { backend: undefined };
    }

    if (req.queryKey.clientID) {
      cv.Config.clientID = req.queryKey.clientID;
    }

    if (req.queryKey.design) {
      cv.Config.clientDesign = req.queryKey.design;
    }

    if (req.queryKey.startpage) {
      cv.Config.startpage = req.queryKey.startpage;
    }

    if (req.queryKey.reportErrors) {
      cv.Config.enableErrorReporting(true);
    }

    // store for later usage
    cv.Config.request = req;

    if (qx.core.Environment.get('cv.testMode') !== false) {
      cv.Config.testMode = true;
    } else if (req.queryKey.testMode) {
      cv.Config.testMode = req.queryKey.testMode === 'true' || req.queryKey.testMode === '1';
    }

    // propagate to the client
    cv.io.Client.TEST_MODE = cv.Config.testMode;

    if (req.queryKey.config) {
      cv.Config.configSuffix = req.queryKey.config;
    }

    if (req.queryKey.forceReload) {
      cv.Config.forceReload = req.queryKey.forceReload !== 'false'; // true unless set to false
    }

    if (req.queryKey.reporting) {
      cv.Config.reporting = req.queryKey.reporting === 'true';
    }

    // caching is only possible when localStorage is available
    if (qx.core.Environment.get('html.storage.local') === false) {
      cv.Config.enableCache = false;
      qx.log.Logger.warn(
        statics,
        'localStorage is not available in your browser. Some advanced features, like caching will not work!'
      );
    } else if (req.queryKey.enableCache === 'invalid') {
      cv.ConfigCache.clear(cv.Config.configSuffix);
      cv.Config.enableCache = true;
    } else {
      cv.Config.enableCache = req.queryKey.enableCache
        ? req.queryKey.enableCache === 'true'
        : !qx.core.Environment.get('qx.debug');
    }

    cv.Config.enableLogging = qx.core.Environment.get('html.console');
    if (req.queryKey.log === 'false') {
      cv.Config.enableLogging = false;
    } else if (req.queryKey.log === 'true') {
      cv.Config.enableLogging = true;
    }

    cv.Config.loadManager = cv.Config.request.queryKey.manager || window.location.hash === '#manager';
    cv.Config.managerOptions = {
      action: cv.Config.request.queryKey.open ? 'open' : '',
      data: cv.Config.request.queryKey.open ? cv.Config.request.queryKey.open : undefined
    };

    // "Bug"-Fix for ID: 3204682 "Caching on web server"
    // Config isn't a real fix for the problem as that's part of the web browser,
    // but
    // it helps to avoid the problems on the client, e.g. when the config file
    // has changed but the browser doesn't even ask the server about it...
    cv.Config.forceReload = true;

    if (req.queryKey.forceDevice) {
      // overwrite detection when set by URL
      switch (req.queryKey.forceDevice) {
        case 'mobile':
          cv.Config.mobileDevice = true;
          break;

        case 'nonmobile':
          cv.Config.mobileDevice = false;
          break;
      }
    }

    // Disable features that aren't ready yet
    // Config can be overwritten in the URL with the parameter "maturity"

    if (req.queryKey.maturity) {
      cv.Config.url_maturity = req.queryKey.maturity;
      if (!isNaN(cv.Config.url_maturity - 0)) {
        cv.Config.use_maturity = cv.Config.url_maturity - 0; // given directly as number
      } else {
        cv.Config.use_maturity = statics.Maturity[cv.Config.url_maturity]; // or as the ENUM name
      }
    }

    if (isNaN(cv.Config.use_maturity)) {
      cv.Config.use_maturity = statics.Maturity.release; // default to release
    }

    cv.Config.useServiceWorker = 'serviceWorker' in navigator && (req.protocol === 'https' || req.host === 'localhost');

    if (cv.Config.useServiceWorker) {
      if (qx.core.Environment.get('qx.debug')) {
        // disable service worker in dev environment unless the user wants it
        cv.Config.useServiceWorker = req.queryKey.worker === 'true';
      }
    }
  }
});
